import type {
  ConditionOperator,
  JobChainTemplateConditionField,
  TemplateCondition,
} from '@/api/JobChainTemplates';
import type { TFunc } from '@/translations';

const fieldLabels: Record<string, Parameters<TFunc>[0]> = {
  engineType: 'jobChainTemplateConditionFieldEngineType',
  transportMode: 'jobChainTemplateConditionFieldTransportMode',
  batteryHVChargeRequired:
    'jobChainTemplateConditionFieldBatteryHVChargeRequired',
  battery12VChargeRequired:
    'jobChainTemplateConditionFieldBattery12VChargeRequired',
};

const operatorLabels: Record<ConditionOperator, Parameters<TFunc>[0]> = {
  Equals: 'jobChainTemplateConditionOperatorEquals',
  NotEquals: 'jobChainTemplateConditionOperatorNotEquals',
  GreaterThan: 'jobChainTemplateConditionOperatorGreaterThan',
  GreaterOrEqual: 'jobChainTemplateConditionOperatorGreaterOrEqual',
  LessThan: 'jobChainTemplateConditionOperatorLessThan',
  LessOrEqual: 'jobChainTemplateConditionOperatorLessOrEqual',
  Contains: 'jobChainTemplateConditionOperatorContains',
  NotContains: 'jobChainTemplateConditionOperatorNotContains',
  StartsWith: 'jobChainTemplateConditionOperatorStartsWith',
  EndsWith: 'jobChainTemplateConditionOperatorEndsWith',
  In: 'jobChainTemplateConditionOperatorIn',
  NotIn: 'jobChainTemplateConditionOperatorNotIn',
  IsNull: 'jobChainTemplateConditionOperatorIsNull',
  IsNotNull: 'jobChainTemplateConditionOperatorIsNotNull',
};

export const conditionFieldLabel = (key: string, t: TFunc) =>
  Object.prototype.hasOwnProperty.call(fieldLabels, key)
    ? t(fieldLabels[key])
    : key;
export const conditionOperatorLabel = (
  operator: ConditionOperator,
  t: TFunc,
) =>
  Object.prototype.hasOwnProperty.call(operatorLabels, operator)
    ? t(operatorLabels[operator])
    : operator;
export const isNullCondition = (operator: ConditionOperator) =>
  operator === 'IsNull' || operator === 'IsNotNull';
export const isMultipleCondition = (operator: ConditionOperator) =>
  operator === 'In' || operator === 'NotIn';
export const conditionValues = (value: string | null) =>
  (value ?? '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);

export const nextConditionGroupId = (conditions: TemplateCondition[]) => {
  const used = new Set(conditions.map((condition) => condition.groupId));
  let next = 1;
  while (used.has(next)) next += 1;
  return next;
};

export const createEdgeCondition = (
  groupId: number,
  field: JobChainTemplateConditionField,
): TemplateCondition => ({
  id: null,
  groupId,
  name: field.key,
  condition: field.operators[0],
  value: isNullCondition(field.operators[0])
    ? null
    : (field.allowedValues[0] ?? ''),
});

export const edgeConditionSummary = (
  conditions: TemplateCondition[],
  t: TFunc,
) => {
  if (!conditions.length) return t('jobChainTemplateConditionAlways');
  const groups = [...new Set(conditions.map((condition) => condition.groupId))];
  return groups
    .map((groupId) => {
      const rows = conditions.filter(
        (condition) => condition.groupId === groupId,
      );
      const text = rows
        .map((condition) => {
          const labels = {
            field: conditionFieldLabel(condition.name, t),
            operator: conditionOperatorLabel(condition.condition, t),
          };
          return isNullCondition(condition.condition)
            ? t('jobChainTemplateConditionSummaryWithoutValue', labels)
            : t('jobChainTemplateConditionSummaryWithValue', {
                ...labels,
                value: condition.value ?? t('jobChainTemplateConditionNotSet'),
              });
        })
        .join(` ${t('jobChainTemplateConditionAnd')} `);
      return groups.length > 1 && rows.length > 1
        ? t('jobChainTemplateConditionSummaryGroup', { conditions: text })
        : text;
    })
    .join(` ${t('jobChainTemplateConditionOr')} `);
};
